import { useForm, usePage } from "@inertiajs/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"

interface PageProps {
  errors?: Record<string, string>
  [key: string]: unknown
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export default function New() {
  const { t } = useTranslation()
  const { data, setData, post, processing, errors: formErrors } = useForm({
    name: "",
    slug: "",
    domain: "",
  })
  const { errors: sharedErrors } = usePage<PageProps>().props
  const errors = { ...sharedErrors, ...formErrors }

  useEffect(() => {
    setData("slug", slugify(data.name))
  }, [data.name])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/accounts")
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("createAccount.title")}</CardTitle>
          <CardDescription>{t("createAccount.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t("createAccount.nameLabel")}</Label>
              <Input
                id="name"
                type="text"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder={t("createAccount.namePlaceholder")}
                required
              />
              <FormError error={errors.name} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">{t("createAccount.slugLabel")}</Label>
              <Input
                id="slug"
                type="text"
                value={data.slug}
                onChange={(e) => setData("slug", e.target.value)}
                placeholder={t("createAccount.slugPlaceholder")}
                required
              />
              <p className="text-xs text-muted-foreground">{t("createAccount.slugHint")}</p>
              <FormError error={errors.slug} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="domain">
                {t("createAccount.domainLabel")}
                <span className="text-muted-foreground ml-1">({t("common.optional")})</span>
              </Label>
              <Input
                id="domain"
                type="text"
                value={data.domain}
                onChange={(e) => setData("domain", e.target.value)}
                placeholder={t("createAccount.domainPlaceholder")}
              />
              <p className="text-xs text-muted-foreground">{t("createAccount.domainHint")}</p>
              <FormError error={errors.domain} />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" asChild>
                <a href="/dashboard">{t("common.cancel")}</a>
              </Button>
              <Button type="submit" className="flex-1" disabled={processing}>
                {processing ? t("createAccount.submitting") : t("createAccount.submit")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
